import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  LayoutDashboard,
  Upload,
  AlertTriangle,
  Users,
  FileText,
  LogOut,
  Settings,
  Menu,
  Search,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const navigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: LayoutDashboard,
      show: true,
      keywords: ['dashboard', 'home', 'overview', 'stats', 'statistics', 'files', 'anomalies', 'recent']
    },
    {
      name: 'Upload Files',
      href: '/upload',
      icon: Upload,
      show: hasRole(['admin', 'employee']),
      keywords: ['upload', 'file', 'excel', 'xlsx', 'import', 'add', 'new']
    },
    {
      name: 'Anomaly Results',
      href: '/results',
      icon: AlertTriangle,
      show: true,
      keywords: ['anomaly', 'results', 'detection', 'issues', 'errors', 'analysis', 'trial balance']
    },
    {
      name: 'Purchase Reconciliation',
      href: '/purchase-reconciliation',
      icon: Settings,
      show: true,
      keywords: ['purchase', 'reconciliation', 'gstr2b', 'compare', 'diff']
    },
    {
      name: 'Sales Reconciliation',
      href: '/sales-reconciliation',
      icon: Settings,
      show: true,
      keywords: ['sales', 'reconciliation', 'gstr1', 'compare', 'diff']
    },
    {
      name: 'Exceptional Balance',
      href: '/exceptional-balance',
      icon: Settings,
      show: true,
      keywords: ['exceptional', 'balance', 'trial', 'golden', 'rules', 'validate']
    },
    {
      name: 'Opening Scrutiny',
      href: '/opening-scrutiny',
      icon: Settings,
      show: true,
      keywords: ['opening', 'scrutiny', 'balance', 'previous', 'current', 'year']
    },
    {
      name: 'AI Document Analyzer',
      href: '/document-analyzer',
      icon: FileText,
      show: true,
      keywords: ['ai', 'document', 'analyzer', 'pdf', 'chat', 'ask', 'extract']
    },
    {
      name: 'User Management',
      href: '/users',
      icon: Users,
      show: hasRole('admin'),
      keywords: ['users', 'management', 'admin', 'employees', 'accounts', 'permissions', 'roles']
    },
    {
      name: 'Audit Logs',
      href: '/audit',
      icon: FileText,
      show: hasRole(['admin', 'auditor']),
      keywords: ['audit', 'logs', 'history', 'activity', 'tracking', 'security']
    }
  ];

  // Global search functionality
  const handleSearch = (term) => {
    setSearchTerm(term);

    if (term.trim() === '') {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const results = navigation
      .filter(item => item.show)
      .filter(item => {
        const searchLower = term.toLowerCase();
        return item.name.toLowerCase().includes(searchLower) ||
          item.keywords.some(keyword => keyword.toLowerCase().includes(searchLower));
      })
      .map(item => ({
        ...item,
        type: 'navigation'
      }));

    // Add additional search categories (you can extend this)
    const additionalResults = [];

    if (term.toLowerCase().includes('profile') || term.toLowerCase().includes('setting')) {
      additionalResults.push({
        name: 'Profile Settings',
        href: '/profile',
        icon: Settings,
        type: 'settings',
        keywords: ['profile', 'settings', 'account', 'preferences']
      });
    }

    setSearchResults([...results, ...additionalResults]);
    setShowSearchResults(true);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  const navigateToResult = (href) => {
    navigate(href);
    clearSearch();
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-300 fixed w-full z-50">
        <div className="max-w-full px-6">
          <div className="flex items-center h-16 relative">
            {/* Sidebar Toggle Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleSidebar}
              className="mr-4 md:hidden"
              data-testid="sidebar-toggle"
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Logo - Left Side */}
            <div className="flex items-center space-x-3 flex-shrink-0">
              {/* Enhanced CSS-based Logo */}
              <div className="relative w-12 h-10 flex-shrink-0 bg-white rounded-lg shadow-sm border border-gray-200 p-1">
                {/* Green glow effect */}
                <div className="absolute top-1 left-2 w-8 h-8 bg-green-400 opacity-30 rounded-full blur-sm"></div>
                {/* Yellow lightbulb - more prominent */}
                <div className="relative top-0.5 left-2 w-8 h-8 bg-gradient-to-b from-yellow-300 via-yellow-400 to-yellow-500 rounded-t-full border border-yellow-600 shadow-md">
                  {/* Inner filament pattern */}
                  <div className="absolute top-2 left-1 right-1 space-y-0.5">
                    <div className="h-0.5 bg-yellow-800 opacity-80"></div>
                    <div className="h-0.5 bg-yellow-800 opacity-80"></div>
                  </div>
                </div>
                {/* Bulb base - more visible */}
                <div className="relative top-0.5 left-2 w-8 h-1.5 bg-gray-600 border border-gray-700 shadow-sm"></div>
                {/* Small blue figure - more prominent */}
                <div className="absolute bottom-1 right-2 w-1.5 h-3 bg-blue-600 rounded-t-full shadow-sm"></div>
              </div>
              {/* Logo Text */}
              <div className="hidden lg:flex flex-col">
                <div className="text-xs font-bold text-gray-900 leading-tight">PROPELLIGENCE</div>
                <div className="text-xs text-gray-600 leading-tight">Advisors Private Limited</div>
              </div>
            </div>

            {/* Global Search Bar - Center */}
            <div className="flex-1 max-w-lg mx-4 relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search entire webapp..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10 pr-10 w-full"
                  data-testid="global-search"
                />
                {searchTerm && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearSearch}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>

              {/* Search Results Dropdown */}
              {showSearchResults && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-md shadow-lg border border-gray-200 max-h-64 overflow-y-auto z-50">
                  {searchResults.length > 0 ? (
                    <div className="py-2">
                      {searchResults.map((result, index) => (
                        <button
                          key={index}
                          onClick={() => navigateToResult(result.href)}
                          className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <result.icon className="h-4 w-4 mr-3 text-gray-400" />
                          <span>{result.name}</span>
                          <span className="ml-auto text-xs text-gray-500 capitalize">{result.type}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="py-4 px-4 text-sm text-gray-500">
                      No results found for "{searchTerm}"
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Company Name - Hidden on smaller screens */}
            <div className="hidden xl:block text-center flex-shrink-0">
              <h1 className="text-lg font-bold text-gray-900">Propelligence Advisors Private Limited</h1>
              <p className="text-xs text-gray-600">CIN No : U70200MH2024PTC428821</p>
            </div>

            {/* Header cleaned - user profile moved to sidebar */}
          </div>
        </div>
      </header>

      <div className="flex h-screen">
        {/* Mobile sidebar backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-30 md:hidden bg-black bg-opacity-50"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <nav className={`
          fixed inset-y-0 left-0 z-40 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 md:static md:inset-0
          ${sidebarOpen ? 'w-64' : 'md:w-16'}
          flex flex-col pt-16 h-screen
        `}>
          <div className="flex-1 flex flex-col min-h-0 pb-4 overflow-hidden">
            {/* Sidebar Toggle Button - Desktop */}
            <div className="hidden md:flex justify-end p-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSidebar}
                className="h-8 w-8"
                data-testid="sidebar-collapse-toggle"
              >
                {sidebarOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            </div>

            {/* Main Navigation */}
            <div className="flex-1 px-3 space-y-1 mt-2 min-h-0">
              {navigation.map((item) => {
                if (!item.show) return null;

                const Icon = item.icon;
                const isActive = location.pathname === item.href;

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${isActive
                        ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                    title={!sidebarOpen ? item.name : ''}
                  >
                    <Icon
                      className={`${sidebarOpen ? 'mr-3' : 'mx-auto'} h-5 w-5 ${isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'
                        }`}
                    />
                    {sidebarOpen && <span className="truncate">{item.name}</span>}
                  </Link>
                );
              })}
            </div>

            {/* Bottom Section - User Profile & Settings */}
            <div className="flex-shrink-0">
              <div className="px-3 pb-4">
                <div className={`${sidebarOpen ? 'border-t border-gray-200 pt-4' : ''}`}>
                  {/* User Profile in Sidebar */}
                  {sidebarOpen && (
                    <div className="mb-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <div className="flex items-center px-2 py-2 text-sm rounded-md hover:bg-gray-50 cursor-pointer transition-colors">
                            <Avatar className="h-8 w-8 mr-3">
                              <AvatarFallback className="bg-blue-600 text-white text-xs">
                                {getInitials(user?.full_name || 'User')}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{user?.full_name}</p>
                              <p className="text-xs text-gray-600 capitalize truncate">{user?.role}</p>
                            </div>
                          </div>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-56 ml-2">
                          <div className="flex items-center justify-start gap-2 p-2">
                            <div className="flex flex-col space-y-1 leading-none">
                              <p className="font-medium">{user?.full_name}</p>
                              <p className="w-[200px] truncate text-sm text-gray-600">
                                {user?.email}
                              </p>
                            </div>
                          </div>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem>
                            <Settings className="mr-2 h-4 w-4" />
                            <span>Settings</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                            <LogOut className="mr-2 h-4 w-4" />
                            <span>Log out</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}

                  {/* Collapsed sidebar - show only avatar */}
                  {!sidebarOpen && (
                    <div className="mb-4 flex justify-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="bg-blue-600 text-white text-xs">
                                {getInitials(user?.full_name || 'User')}
                              </AvatarFallback>
                            </Avatar>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-56 ml-2">
                          <div className="flex items-center justify-start gap-2 p-2">
                            <div className="flex flex-col space-y-1 leading-none">
                              <p className="font-medium">{user?.full_name}</p>
                              <p className="w-[200px] truncate text-sm text-gray-600">
                                {user?.email}
                              </p>
                            </div>
                          </div>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem>
                            <Settings className="mr-2 h-4 w-4" />
                            <span>Settings</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                            <LogOut className="mr-2 h-4 w-4" />
                            <span>Log out</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}

                  {/* Profile Settings Link */}
                  <Link
                    to="/profile"
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${location.pathname === '/profile'
                        ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    data-testid="nav-profile-settings"
                    title={!sidebarOpen ? 'Profile Settings' : ''}
                  >
                    <Settings
                      className={`${sidebarOpen ? 'mr-3' : 'mx-auto'} h-5 w-5 ${location.pathname === '/profile' ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'
                        }`}
                    />
                    {sidebarOpen && <span className="truncate">Profile Settings</span>}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* Main content */}
        <div className={`flex flex-col flex-1 transition-all duration-300 ${sidebarOpen ? 'md:ml-[50px]' : 'md:ml-16'} h-screen`}>
          <main className="flex-1 overflow-y-auto pt-16">
            <div className="py-4 pl-1 pr-6">
              {children}
            </div>
          </main>
        </div>

        {/* Click outside to close search results */}
        {showSearchResults && (
          <div
            className="fixed inset-0 z-20"
            onClick={() => setShowSearchResults(false)}
          />
        )}
      </div>
    </div>
  );
};

export default Layout;
